import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Provider } from 'react-redux';

import { TestUIProvider } from './components/helper/TestUIWrapper';
import { UserAnalyticsProvider } from './components/helper/UserAnalytics';
import Routes from './components/route/Routes';
import { createAppQueryClient } from './config/createAppQueryClient';
import { registerQueryClient } from './config/queryClientRegistry';
import { useCurrentUser } from './hooks/useCurrentUser';
import { HirePortContextProvider } from './hooks/useHirePortContext';
import store from './store';
import {
  applyEmbedUrlTokenIfPresent,
  restoreSessionIfNeeded,
} from './store/sessionBootstrap';
import { setDisplayTimezone } from './utils/displayTimezone';
import { initializeGlobalNumberInputProtection } from './utils/globalNumberInputProtection';
import { initDeferredAnalytics } from './utils/initDeferredAnalytics';

import './index.css';
import './i18n';

const queryClient = createAppQueryClient();

registerQueryClient(queryClient);

const DisplayTimezoneSync = ({ children }: { children: ReactNode }) => {
  const { user } = useCurrentUser();
  setDisplayTimezone(user?.default_timezone ?? null);
  return children;
};

// Apply embed `?token=` before the first restore so leftover localStorage JWTs
// are not fetched. Once per JS realm — not a render side effect.
applyEmbedUrlTokenIfPresent();
restoreSessionIfNeeded();

function App() {
  useEffect(() => {
    initDeferredAnalytics();
    initializeGlobalNumberInputProtection();
  }, []);

  return (
    <div className="App">
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <HirePortContextProvider>
            <DisplayTimezoneSync>
              <UserAnalyticsProvider />
              <TestUIProvider>
                <Routes />
              </TestUIProvider>
            </DisplayTimezoneSync>
          </HirePortContextProvider>
        </QueryClientProvider>
      </Provider>
    </div>
  );
}

export default App;
